import { atCopy, axisLabel } from './home-feed-activity-copy';
import type { OfferCountdownView } from './home-feed-offers-view';

/** The offer countdown: offerable now, or how many more homes the member must engage with. */
export function countdownCopy(c: OfferCountdownView): string {
    if (c.offerableNow) return 'Offerable now.';
    const more = c.moreHomes === 1 ? '1 more home' : `${c.moreHomes} more homes`;
    return `${more} to engage with before the next offer (${c.homesSinceLastDecision} of ${c.cooldownHomes} since the last decision).`;
}

/** What last started the wait (or, once the wait is over, the last decision). Either kind of offer can. */
export function lastDecisionCopy(c: OfferCountdownView): string {
    if (!c.lastDecisionKey) return 'No offer has been decided yet, so nothing is counting.';
    const widening = c.lastDecisionIsWidening ? ' (a widening)' : '';
    const when = c.lastDecisionUtc ? ` on ${atCopy(c.lastDecisionUtc)}` : '';
    const prefix = c.offerableNow ? 'Last decision:' : 'Started by';
    switch (c.lastDecisionKind) {
        case 'YES':
            return `${prefix} a "Yes" to ${c.lastDecisionKey}${widening}${when}.`;
        case 'NO':
            return `${prefix} a "No" to ${c.lastDecisionKey}${widening}${when}.`;
        case 'SWIPE':
            return `${prefix} a swipe-away of ${c.lastDecisionKey}${widening}${when}.`;
        default:
            return `${prefix} a decision on ${c.lastDecisionKey}${widening}${when}.`;
    }
}

export function inferenceOutcomeCopy(outcome: string, interested: number, min: number): string {
    switch (outcome) {
        case 'ON_OFFER':
            return 'An Inference is on offer.';
        case 'COOLING_DOWN':
            return 'Cooling down: the next offer waits on the countdown below.';
        case 'NOT_ENOUGH_INTERESTED_HOMES':
            return `Not enough interested homes yet: ${interested} of the ${min} needed.`;
        case 'SEARCH_UNAVAILABLE':
            return 'The Search could not be read, so no Inference was worked out.';
        case 'NO_PATTERN_FOUND':
            return 'No pattern found in the homes the member engaged with.';
        case 'EVERY_SUGGESTION_SETTLED':
            return 'Every suggestion is already settled (confirmed, turned down, or already held).';
        default:
            return outcome;
    }
}

/** Why there is, or is not, a widening offer: one distinct sentence per outcome. */
export function wideningOutcomeCopy(
    outcome: string,
    holdingInferenceKey: string | null,
    interested: number,
    min: number,
): string {
    switch (outcome) {
        case 'ON_OFFER':
            return 'A Widening offer is on offer.';
        case 'NO_ASSESSED_AXES':
            return "Enough engaged wildcards, but no axis could be assessed (the homes' facts were not readable).";
        case 'UNSCORED':
            return 'The feed offers no widening: the household has no scores.';
        case 'RENTING':
            return 'The feed offers no widening: this is a rent Search.';
        case 'NOT_COVERED':
            return 'The feed offers no widening: the index does not serve this Search.';
        case 'BELOW_BAR':
            return interested < min
                ? `Below the bar: ${interested} interested wildcards of the ${min} needed.`
                : `Below the bar on every axis: fewer than ${min} interested on any one axis, or too many skipped (see Axes).`;
        case 'NOTHING_TO_WIDEN':
            return 'Nothing to widen: the Search already covers what the interested homes share.';
        case 'AXES_TURNED_OFF':
            return 'Every axis the member engaged with is switched off.';
        case 'INFERENCE_HOLDS_SLOT':
            return holdingInferenceKey
                ? `An Inference holds the single sheet slot: ${holdingInferenceKey}.`
                : 'An Inference holds the single sheet slot.';
        case 'COOLING_DOWN':
            return "Cooling down: waiting on the widening's own engaged-homes countdown below.";
        case 'SUPPRESSED':
            return "No candidate can be offered: at least one was turned down within the server's 30-day quiet period and the rest are already confirmed (see Candidates).";
        case 'ALREADY_CONFIRMED':
            return 'Every candidate is already confirmed.';
        default:
            return outcome;
    }
}

export function candidateStatusCopy(status: string): string {
    switch (status) {
        case 'ELIGIBLE':
            return 'eligible';
        case 'CONFIRMED':
            return 'confirmed';
        case 'REJECTED':
            return 'turned down';
        case 'ALREADY_HELD':
            return 'already held';
        default:
            return status;
    }
}

export function axisStatusCopy(status: string): string {
    switch (status) {
        case 'OFFER':
            return 'offer';
        case 'TURNED_OFF':
            return 'switched off';
        case 'BELOW_BAR':
            return 'below the bar';
        case 'NOTHING_TO_WIDEN':
            return 'nothing to widen';
        case 'NOT_ASSESSED':
            return 'not assessed';
        default:
            return status;
    }
}

export function rejectionCopy(r: { key: string; rejectedUtc: string; mayReturnUtc: string; stillQuiet: boolean }): string {
    return `${r.key}: a "No" on ${atCopy(r.rejectedUtc)}; ${r.stillQuiet ? 'may return' : 'could be offered again from'} ${atCopy(r.mayReturnUtc)}.`;
}

export function dismissalCopy(d: { key: string; dismissedUtc: string }): string {
    return `${d.key}: swiped away on ${atCopy(d.dismissedUtc)} (seen, suppresses nothing).`;
}

export function confirmationCopy(c: { key: string; confirmedUtc: string }): string {
    return `${c.key}: confirmed on ${atCopy(c.confirmedUtc)}.`;
}

export function axisCountCopy(a: { axis: string; status: string; interested: number; skipped: number }): string {
    return `${axisLabel(a.axis)}: ${axisStatusCopy(a.status)} (${a.interested} interested, ${a.skipped} skipped).`;
}

/**
 * A widening confirmed and awaiting agreement, worded per direction. ⛔ Only PARTNER (the inspected
 * member stated it) names the widening key; THIS_MEMBER and ANOTHER_MEMBER never name a key or a member.
 */
export function pendingCopy(p: {
    waitingOn: string;
    wideningKey: string | null;
    kind: string;
    subject: string;
    value: string;
    statedUtc: string;
}): string {
    const what = `${p.subject} (${p.kind}): ${p.value}`;
    const when = atCopy(p.statedUtc);
    switch (p.waitingOn) {
        case 'PARTNER':
            return `Stated by this member${p.wideningKey ? ` (${p.wideningKey})` : ''} on ${when}; waiting on a partner to agree. ${what}. Not applied to the household yet.`;
        case 'THIS_MEMBER':
            return `Stated by a partner on ${when}; waiting on THIS member to agree. ${what}. Not applied to the household yet.`;
        case 'ANOTHER_MEMBER':
            return `Stated by a partner on ${when}; waiting on another member (this member already agreed, or was never asked). ${what}. Not applied to the household yet.`;
        default:
            return `${p.waitingOn}: ${what}`;
    }
}
